// Edge-box hardware kinds shown in the Agents picker + setup wizard. Deliberately a frontend
// constant — not a catalog table and not a SKU table (see backend CONTEXT.md "Hardware model").
// Changing this list is a frontend edit + redeploy; the values here are expected to evolve as
// hardware tiers change, so nothing else should hard-code them.

export type EdgeBoxKind = {
  /** SKU name — the dropdown option and (for a fixed kind) the value stored in hardware_model. */
  name: string;
  /** Underlying hardware spec shown in the read-only description; the operator types it for Custom. */
  description: string;
  /** Fixed camera capacity (allowed_number_of_cameras); null for Custom (operator types it). */
  capacity: number | null;
  /** Custom = free-text description + typed capacity. */
  custom: boolean;
};

const CUSTOM_KIND: EdgeBoxKind = {
  name: "Custom (Debian/Raspberry Pi OS)",
  description: "",
  capacity: null,
  custom: true,
};

// Two options only (operator, 2026-08-24): buy our device, or bring your own box.
// Agents provisioned under the retired kind names (Mini/Regular/Mega) resolve to
// Custom with their original text preserved — matchKindIndex's designed fallback.
export const EDGE_BOX_KINDS: readonly EdgeBoxKind[] = [
  {
    name: "Edge Agent - Regular",
    description: "Raspberry Pi 4B 4GB, 32GB SD card",
    capacity: 32,
    custom: false,
  },
  CUSTOM_KIND,
];

export const DEFAULT_EDGE_BOX_KIND_INDEX = 0;

/** Index-safe accessor — falls back to Custom for an out-of-range index. */
export function kindAt(index: number): EdgeBoxKind {
  return EDGE_BOX_KINDS[index] ?? CUSTOM_KIND;
}

/**
 * Resolve a stored hardware_model to a dropdown index for editing. Exact-match a fixed kind by
 * name; otherwise fall back to Custom — which covers genuine custom boxes AND fixed kinds whose
 * name changed in this constant since the agent was provisioned (their old text is preserved and
 * shown in the Custom description field).
 */
export function matchKindIndex(hardwareModel: string | null): number {
  if (hardwareModel) {
    const i = EDGE_BOX_KINDS.findIndex((k) => !k.custom && k.name === hardwareModel);
    if (i >= 0) return i;
  }
  return EDGE_BOX_KINDS.indexOf(CUSTOM_KIND);
}
